interface WordCloudProps {
  frequencies: Record<string, number>;
}

// WHY: Lightweight, dependency-free word cloud. Chart.js draws on canvas and can't
// inherit CSS theme variables, so a pure DOM flex-wrap with computed font sizes is
// simpler and stays theme-aware. Font size scales with how many party members know
// the item; words are rendered as plain text (React-escaped) so custom/homebrew
// values can't inject markup.
export default function WordCloud({ frequencies }: WordCloudProps) {
  const entries = Object.entries(frequencies).filter(([, count]) => count > 0);
  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-dnd-muted">
        No data yet — add languages or tools to characters in this party.
      </div>
    );
  }

  const counts = entries.map(([, c]) => c);
  const maxCount = Math.max(...counts);

  // WHY: Fixed min/max bounds keep single-item clouds legible (not microscopic)
  // and prevent a single ubiquitous language from dominating the whole panel.
  const MIN_SIZE = 0.9;
  const MAX_SIZE = 2.5;
  const sizeFor = (count: number) =>
    maxCount > 1
      ? MIN_SIZE + ((count - 1) / (maxCount - 1)) * (MAX_SIZE - MIN_SIZE)
      : (MIN_SIZE + MAX_SIZE) / 2;

  // WHY: Sort by frequency desc so the layout is stable across renders and the
  // most-known items lead the wrap order.
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 py-6">
      {sorted.map(([word, count]) => (
        <span
          key={word}
          title={`${count} ${count === 1 ? 'character' : 'characters'} know this`}
          style={{ fontSize: `${sizeFor(count)}rem` }}
          className="text-dnd-accent font-semibold leading-tight cursor-default"
        >
          {word}
        </span>
      ))}
    </div>
  );
}
